import React, { FC, useCallback, useEffect, useState } from 'react';
import { TextField } from '@material-ui/core';
import { Autocomplete, Skeleton } from '@material-ui/lab';
import { LegacyCustomer } from 'src/interfaces/api/Customer';
import useAuth from 'src/hooks/api/useAuth';

interface Props {
  value?: number;
  error?: boolean;
  size?: 'small' | 'medium';
  helperText?: string;
  className?: any;
  onChange: (value?: number) => void;
}

const LegacyCustomerAutocomplete: FC<Props> = React.forwardRef<any, Props>(
  ({ onChange, value, size = 'medium', className, helperText }, ref) => {
    const [customers, setCustomers] = useState<LegacyCustomer[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>();
    const { fetch } = useAuth();

    const getCustomer = useCallback((customerId: number) => customers.find((customer) => customer.id === customerId), [
      customers,
    ]);

    const getCustomerLabel = useCallback(
      (customerId: number) => {
        const customer = getCustomer(customerId);
        return customer?.installationSite?.socialReason || '';
      },
      [customers]
    );

    const fetchCustomers = (): Promise<LegacyCustomer[]> => {
      const queryURL = '/api/customer/';
      const graphQL = '{ customers{ items{ id, installationSite{ socialReason } } } }';

      return fetch(queryURL + graphQL, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        method: 'GET',
      })
        .then((resp) => resp.json())
        .then((data) => {
          if (data['data']['customers']['items']) {
            return data['data']['customers']['items'];
          }

          throw new Error('Aucun client trouvé ...');
        });
    };

    useEffect(() => {
      let active = true;

      if (!loading) {
        return undefined;
      }

      (async () => {
        try {
          setLoading(true);
          const response = await fetchCustomers();

          if (active) {
            setCustomers(response);
            setLoading(false);
          }
        } catch (e) {
          setError(e.message);
        }
      })();

      return () => {
        active = false;
      };
    }, [loading]);

    const handleChange = (event: any, value: number | null) => {
      onChange(value ?? undefined);
    };

    if (loading) {
      return <Skeleton height={50} />;
    }

    if (error) {
      return <div style={{ color: '#f44336' }}>{error}</div>;
    }

    return (
      <Autocomplete
        ref={ref}
        size={size}
        className={className}
        value={value || 0}
        onChange={handleChange}
        options={customers.map((customer) => customer.id)}
        getOptionLabel={(customerId) => getCustomerLabel(customerId)}
        renderInput={(params) => <TextField {...params} label='Client' variant='outlined' fullWidth />}
      />
    );
  }
);

export default React.memo(LegacyCustomerAutocomplete);
